import type { SelectedOptions } from '@corratech/pylot-variant-selector'
import { useOptionSelector } from '@corratech/pylot-variant-selector'
import type {
    ConfigurableAttributeOption,
    ConfigurableProductOptionsValues,
    ConfigurableVariant,
    Maybe,
    MediaGalleryInterface,
    SimpleProduct
} from '@pylot-data/fwrdschema'
import type { ConfigurableProductOptions } from '@pylot-data/pylotschema'
import { useTranslation } from 'next-i18next/pages'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { ProductType } from './use-product-fetch'
import { getButtonLabel } from './utils/getButtonLabel'
import { getMediaGalleryEntries } from './utils/getMediaGalleryEntries'

// Redefining here, as importing original enum as a value from (fwrd/pylot)schema does not work
export enum ProductStockStatus {
    InStock = 'IN_STOCK',
    OutOfStock = 'OUT_OF_STOCK'
}

export enum OptionType {
    VisualSwatch = 'VisualSwatch',
    TextSwatch = 'TextSwatch',
    Dropdown = 'Dropdown',
    Other = 'Other'
}

export type Option = {
    displayName: string
    code: string
    values: Maybe<ConfigurableProductOptionsValues[]>
    type: OptionType | null
}

export enum ButtonLabel {
    OUT_OF_STOCK = 'Out of stock',
    SELECT_A_VARIANT = 'Select a variant',
    ADD_TO_CART = 'Add to cart',
    UPDATE_CART_ITEM = 'Update'
}

export enum ProductVariant {
    NOT_EXIST = -1,
    NOT_SELECTED = -2
}

export type UseProductUIReturn<ProductType> = {
    setSelectedOptions: Dispatch<SetStateAction<SelectedOptions>>
    selectedOptions: SelectedOptions
    options: Option[]
    variantId: number
    variant: SimpleProduct | ProductType
    mediaGalleryEntries: Array<Maybe<MediaGalleryInterface>>
    buttonLabel: string
    isButtonDisabled: boolean
    isConfigOptionSelected: boolean
    selectedOptionUIDs: string[]
    quantity: number
    setQuantity: Dispatch<SetStateAction<number>>
}

export type UseProductUi<ProductType> = (
    product: ProductType,
    options?: {
        preselectedOptions?: Record<string, string>
        buttonActionLabel?: ButtonLabel
    }
) => UseProductUIReturn<ProductType>

export const getVariantBySelectedOptions = (
    product: any,
    selectedOptions: any
) => {
    const variantId = (product.variants as ConfigurableVariant[]).findIndex(
        (prodVar) => {
            const { attributes: variantAttributes } = prodVar
            return (variantAttributes as ConfigurableAttributeOption[]).every(
                (attr) =>
                    selectedOptions[attr.code as keyof SelectedOptions] ===
                    attr.label
            )
        }
    )

    return variantId
}

export const useProductUI: UseProductUi<any> = (
    product,
    { preselectedOptions, buttonActionLabel = ButtonLabel.ADD_TO_CART } = {
        buttonActionLabel: ButtonLabel.ADD_TO_CART
    }
) => {
    const { t } = useTranslation(['common'])
    const [variantId, setVariantId] = useState<number>(
        ProductVariant.NOT_SELECTED
    )

    const [quantity, setQuantity] = useState<number>(1)

    const productType = product?.__typename as ProductType | undefined
    const isConfig = productType === ProductType.CONFIGURABLE_PRODUCT

    const variant = product?.variants?.[variantId]?.product ?? product

    const { options, selectedOptions, setSelectedOptions } = useOptionSelector(
        product?.configurable_options as ConfigurableProductOptions[],
        preselectedOptions,
        true,
        product?.variants
    )

    const isButtonDisabled =
        variantId === ProductVariant.NOT_EXIST ||
        (product as SimpleProduct)?.stock_status ===
            ProductStockStatus.OutOfStock ||
        variant?.stock_status === ProductStockStatus.OutOfStock ||
        (isConfig && variantId === ProductVariant.NOT_SELECTED)

    const isConfigOptionSelected =
        isConfig && variantId === ProductVariant.NOT_SELECTED

    const { attributes } = product?.variants?.[variantId] ?? {}

    const selectedOptionUIDs =
        (attributes as ConfigurableAttributeOption[])?.map(
            (attr) => attr?.uid ?? ''
        ) ?? []

    //Component mount hook || option change
    useEffect(() => {
        const currentlySelectedOptions = Object.values(selectedOptions).filter(
            (v) => !!v
        )

        const areAllOptionsSelected =
            currentlySelectedOptions.length ===
            Object.keys(selectedOptions).length

        // if no configurable product
        if (!product?.variants || !areAllOptionsSelected) {
            setVariantId(ProductVariant.NOT_SELECTED)
        } else {
            const newVariantId = getVariantBySelectedOptions(
                product,
                selectedOptions
            )

            setVariantId(newVariantId)
        }
    }, [product, selectedOptions])

    //get the media galleries to render the gallery component
    const mediaGalleryEntries = useMemo(
        () => getMediaGalleryEntries(product, variantId),
        [product, variantId]
    )

    //get button text status
    const buttonLabel = useMemo(() => {
        const label = getButtonLabel(
            isConfig,
            product?.stock_status,
            variant?.stock_status,
            variantId,
            buttonActionLabel
        )
        return t(label)
        // TODO: check if that brings any benefit
    }, [
        buttonActionLabel,
        isConfig,
        variant?.stock_status,
        product?.stock_status,
        variantId,
        t
    ])

    return {
        setSelectedOptions,
        selectedOptions,
        setQuantity,
        quantity,
        options,
        variantId,
        variant,
        mediaGalleryEntries,
        buttonLabel,
        isButtonDisabled,
        isConfigOptionSelected,
        selectedOptionUIDs
    }
}
